<script setup lang="ts">
  /**
   * Page frame — 03-components/12e-page-frame · OSCloud DS 1.1.47
   */
  withDefaults(
    defineProps<{
      /** No width cap: a page whose primary content is a wide table or a monitoring grid. */
      fluid?: boolean
      /** Content + rail grid. The rail owns stickiness, so `.order-summary--sticky` is a no-op in it. */
      withRail?: boolean
      /** Caps the content column at --bf-page-measure. A measure is a property of a column, never of the page. */
      measure?: boolean
      /**
       * The rail folds at 1280px instead of 1024px. For a frame inside the console shell: the
       * sidebar takes its width before the frame is measured, so at the plain stop the content
       * column ends up narrower than the fixed rail beside it.
       */
      inShell?: boolean
    }>(),
    { fluid: false, withRail: false, measure: false, inShell: false },
  )
</script>

<template>
  <div
    class="page-layout"
    :class="{
      'page-layout--fluid': fluid,
      'page-layout--with-rail': withRail,
      'page-layout--in-shell': inShell,
    }"
  >
    <div v-if="withRail" class="page-layout__content" :class="{ 'page-layout__measure': measure }">
      <slot />
    </div>
    <div v-else :class="{ 'page-layout__measure': measure }">
      <slot />
    </div>
    <div v-if="withRail" class="page-layout__rail" data-sticky-rail>
      <slot name="rail" />
    </div>
  </div>
</template>

<style scoped lang="scss">
  /* The frame does not centre itself: max-width with no auto margin leaves it
     flush with the start of its region, which is what a console page beside a
     sidebar wants. A placement that needs it centred adds margin-inline: auto
     where it places the frame. */
  .page-layout {
    width: 100%;
    max-width: var(--bf-page-content-width);
    padding-inline: var(--bf-page-gutter);
    padding-bottom: var(--bf-sp-5); /* 60px, the page's own trailing space */

    &--fluid {
      max-width: none;
    }

    &--with-rail {
      display: grid;
      grid-template-columns: minmax(0, 1fr) var(--bf-page-rail-width);
      gap: var(--bf-sp-4);
      align-items: start;
    }

    &__content {
      min-width: 0; /* lets a wide table scroll in its own wrapper instead of pushing the grid */
    }

    &__rail {
      position: sticky;
      top: calc(var(--bf-header-h-app) + var(--bf-sp-2));
      display: flex;
      flex-direction: column;
      gap: var(--bf-sp-3);
    }

    &__measure {
      max-width: var(--bf-page-measure);
    }
  }

  /* Inside the console shell the scrollport is DefaultLayout's .layout-content, which already
       starts below the application header, so the rail's offset is a plain gap. Counting the
       header height again pushes the panel a header's height down the page, clear of the title
       it should sit beside; the card measures its offset against the window, where the header
       does overlap the scrollport. The same trade Add Funds makes for its own rail. */
  .page-layout--in-shell .page-layout__rail {
    top: var(--bf-sp-2);
  }

  /* The frame is the outermost element of the page's content region, so nothing
     narrower contains it and a container query would have nothing to watch.
     This is the deliberate exception to the container-query trade the data
     table and the console sidebar make. */
  @media (max-width: 1024px) {
    /* --bf-bp-rail */
    .page-layout--with-rail {
      grid-template-columns: minmax(0, 1fr);
    }

    .page-layout__rail {
      position: static;
    }
  }

  /* Inside the console shell the sidebar is subtracted before the frame is measured, so the
     fold has to come earlier: at 1024–1280px the plain stop leaves the content column narrower
     than the fixed rail it sits beside. This is the page's rail stop moved, not the shell's
     sidebar stop — the two are independent, and conflating them is a real bug. */
  @media (max-width: 1280px) {
    .page-layout--in-shell.page-layout--with-rail {
      grid-template-columns: minmax(0, 1fr);
    }

    .page-layout--in-shell .page-layout__rail {
      position: static;
    }
  }

  @media (max-width: 600px) {
    /* --bf-bp-sm */
    .page-layout {
      padding-inline: var(--bf-page-gutter-narrow);
    }
  }
</style>
